import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  IconButton,
  Slide,
  Snackbar,
  Stack,
  SlideProps,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';
import { chatWebSocket, WSMessage } from '../services/chatWebSocket';
import { AppNotification } from '../types/notification';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI } from '../services/api';

const AUTO_HIDE_MS = 6000;
const MAX_VISIBLE = 4;

interface ToastItem {
  uid: string;
  notif: AppNotification;
}

const SlideTransition = (props: SlideProps) => <Slide {...props} direction="left" />;

const NotificationToast: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<ToastItem[]>([]);
  const counterRef = useRef(0);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = chatWebSocket.onMessage((msg: WSMessage) => {
      if (msg.type !== 'notification') return;
      const notif = (msg as any).notification as AppNotification | undefined;
      if (!notif) return;
      counterRef.current += 1;
      const uid = `${Date.now()}-${counterRef.current}`;
      setItems((prev) => [...prev, { uid, notif }].slice(-MAX_VISIBLE));
    });
    return unsubscribe;
  }, [user]);

  const dismiss = (uid: string) => {
    setItems((prev) => prev.filter((i) => i.uid !== uid));
  };

  const handleClick = async (item: ToastItem) => {
    dismiss(item.uid);
    if (!item.notif.is_read) {
      try {
        await notificationsAPI.markRead([item.notif.id]);
      } catch {
        /* silent */
      }
    }
    if (item.notif.link) navigate(item.notif.link);
  };

  if (!user || items.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 80,
        right: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        pointerEvents: 'none',
      }}
    >
      <Stack spacing={1.25} sx={{ pointerEvents: 'auto' }}>
        {items.map((item) => (
          <Snackbar
            key={item.uid}
            open
            autoHideDuration={AUTO_HIDE_MS}
            onClose={(_, reason) => {
              if (reason === 'clickaway') return;
              dismiss(item.uid);
            }}
            TransitionComponent={SlideTransition}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              position: 'static',
              transform: 'none !important',
              left: 'auto !important',
              right: 'auto !important',
              top: 'auto !important',
            }}
          >
            <Alert
              severity="info"
              variant="filled"
              onClick={() => handleClick(item)}
              icon={
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.18)',
                    width: 36,
                    height: 36,
                  }}
                >
                  <NotificationsActiveIcon fontSize="small" />
                </Avatar>
              }
              action={
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismiss(item.uid);
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              sx={{
                width: 380,
                cursor: item.notif.link ? 'pointer' : 'default',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: 6,
                borderRadius: 2,
                alignItems: 'flex-start',
                '& .MuiAlert-icon': { mr: 1.5, alignItems: 'center' },
                '& .MuiAlert-message': { flex: 1, py: 0.5 },
              }}
            >
              <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
                {item.notif.title}
              </AlertTitle>
              {item.notif.body && (
                <Typography variant="body2" sx={{ opacity: 0.95 }}>
                  {item.notif.body}
                </Typography>
              )}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </Box>
  );
};

export default NotificationToast;
